<script setup lang="ts">
import { area, line, stack } from 'd3-shape';
import { computed } from 'vue';

import { COMPARISON_MODE_LABELS } from '@/shared/lib';
import { AnalyticsComparisonMode } from '@shared/api/graphql/gql/graphql';

import type { ChartGranularity } from './chart-point-window';

import { formatChartAxisLabel, formatChartPointWindow } from './chart-point-window';
import { buildIndexScale, buildLabelIndexes, buildValueScale, buildValueTicks } from './chart-scales';
import { chartSeriesColor } from './chart-series-color';
import ChartCanvas from './ChartCanvas.vue';
import ChartTooltip from './ChartTooltip.vue';
import { useChartCanvas } from './use-chart-canvas';

interface StackedAreaSeries {
  key: string;
  label: string;
  values: number[];
}

interface Props {
  dates: string[];
  granularity?: ChartGranularity;
  highlightKey?: string;
  incompleteIndex?: number;
  /** Сумма прошлого периода по точкам, выровнена с `dates`: пунктирная линия и строка тултипа. */
  previous?: readonly number[];
  series: StackedAreaSeries[];
  /** Подпись для скринридера; по умолчанию график выручки по локациям. */
  title?: string;
  valueFormatter: (value: number) => string;
}

interface StackRow {
  values: Map<string, number>;
}

interface StackAreaPoint {
  lower: number;
  upper: number;
  x: number;
}

interface StackedLayer {
  color: string;
  key: string;
  path: string;
}

const props = withDefaults(defineProps<Props>(), {
  granularity: 'Day',
  highlightKey: undefined,
  incompleteIndex: -1,
  previous: undefined,
  title: undefined,
});

const { activeIndex, activeViewportAnchor, canvas, geometry } = useChartCanvas(
  'stacked-area-chart',
  () => chartTitle.value,
  () => props.dates.length,
);

const warnedNonFiniteSeriesKeys = new Set<string>();

const normalizedSeries = computed(() =>
  props.series.map((currentSeries) => {
    let hasNonFiniteValue = false;
    const values: number[] = [];
    for (const dateIndex of props.dates.keys()) {
      const value = currentSeries.values[dateIndex] ?? 0;
      if (!Number.isFinite(value)) {
        hasNonFiniteValue = true;
        values.push(0);
      } else {
        values.push(Math.max(0, value));
      }
    }

    if (hasNonFiniteValue && !warnedNonFiniteSeriesKeys.has(currentSeries.key)) {
      warnedNonFiniteSeriesKeys.add(currentSeries.key);
      console.warn('[BaseStackedAreaChart] Серия содержит NaN или Infinity.', {
        seriesKey: currentSeries.key,
      });
    }

    return {
      ...currentSeries,
      values,
    };
  }),
);
const currentTotals = computed(() =>
  props.dates.map((_, dateIndex) =>
    normalizedSeries.value.reduce((sum, currentSeries) => sum + (currentSeries.values[dateIndex] ?? 0), 0),
  ),
);
const hasHighlightedSeries = computed(
  () =>
    props.highlightKey !== undefined && props.series.some((currentSeries) => currentSeries.key === props.highlightKey),
);
const xScale = computed(() => buildIndexScale(props.dates.length, [geometry.value.plotLeft, geometry.value.plotRight]));
const previousTotals = computed(() =>
  props.previous ? props.dates.map((_, dateIndex) => Math.max(0, props.previous?.[dateIndex] ?? 0)) : null,
);
const valueScale = computed(() =>
  buildValueScale(
    [...currentTotals.value, ...(previousTotals.value ?? [])],
    [geometry.value.plotBottom, geometry.value.plotTop],
  ),
);
const valueTicks = computed(() => buildValueTicks(valueScale.value));
const visibleLabelIndexes = computed(() => buildLabelIndexes(props.dates.length));
const xForIndex = (index: number): number => xScale.value(index);
const yForValue = (value: number): number => valueScale.value(Math.max(0, value));

const stackedLayers = computed(() => {
  const rows = props.dates.map((_, dateIndex): StackRow => ({
    values: new Map(
      normalizedSeries.value.map((currentSeries) => [currentSeries.key, currentSeries.values[dateIndex] ?? 0]),
    ),
  }));
  const stackedData = stack<StackRow>()
    .keys(normalizedSeries.value.map((currentSeries) => currentSeries.key))
    .value((row, key) => row.values.get(key) ?? 0)(rows);
  const areaGenerator = area<StackAreaPoint>()
    .x((point) => point.x)
    .y0((point) => yForValue(point.lower))
    .y1((point) => yForValue(point.upper));

  return stackedData
    .map((stackedSeries, seriesIndex): StackedLayer | null => {
      const currentSeries = normalizedSeries.value[seriesIndex];

      if (!currentSeries) {
        return null;
      }

      let points = stackedSeries.map((point, dateIndex): StackAreaPoint => ({
        lower: point[0],
        upper: point[1],
        x: xForIndex(dateIndex),
      }));

      if (points.length === 1) {
        const point = points[0];

        if (point) {
          points = [
            { ...point, x: geometry.value.plotLeft },
            { ...point, x: geometry.value.plotRight },
          ];
        }
      }

      return {
        color: chartSeriesColor(seriesIndex),
        key: currentSeries.key,
        path: areaGenerator(points) ?? '',
      };
    })
    .filter((layer): layer is StackedLayer => layer !== null);
});

const previousPath = computed(() => {
  const totals = previousTotals.value;

  if (!totals || totals.length < 2) {
    return '';
  }

  return (
    line<number>()
      .x((_, dateIndex) => xForIndex(dateIndex))
      .y((value) => yForValue(value))(totals) ?? ''
  );
});

/**
 * Прозрачность считается вне `stackedLayers`: подсветка легенды меняет только её,
 * а пересчёт d3-стека и путей при этом не нужен.
 */
const layerOpacity = (key: string): number => {
  if (!hasHighlightedSeries.value) {
    return 0.78;
  }

  return key === props.highlightKey ? 0.92 : 0.16;
};

const incompleteSegment = computed(() => {
  const index = props.incompleteIndex;

  if (index < 0 || index >= props.dates.length) {
    return null;
  }

  const centerX = xForIndex(index);
  const previousX = index > 0 ? xForIndex(index - 1) : geometry.value.plotLeft;
  const nextX = index < props.dates.length - 1 ? xForIndex(index + 1) : geometry.value.plotRight;
  const startX = index === 0 ? geometry.value.plotLeft : (previousX + centerX) / 2;
  const endX = index === props.dates.length - 1 ? geometry.value.plotRight : (centerX + nextX) / 2;

  return {
    width: Math.max(0, endX - startX),
    x: startX,
  };
});

const activePoint = computed(() => {
  const index = activeIndex.value;

  if (index === null) {
    return null;
  }

  const date = props.dates[index];

  if (!date) {
    return null;
  }

  return {
    date,
    index,
    isIncomplete: index === props.incompleteIndex,
    previous: previousTotals.value?.[index] ?? null,
    total: currentTotals.value[index] ?? 0,
    x: xForIndex(index),
  };
});

const GRANULARITY_LABELS = {
  Day: 'по дням',
  Hour: 'по часам',
  Month: 'по календарным месяцам',
  Week: 'по недельным окнам',
} satisfies Record<ChartGranularity, string>;

const INCOMPLETE_LABELS = {
  Day: 'День не закончен',
  Hour: 'Час не закончен',
  Month: 'Месяц не закончен',
  Week: 'Неделя не закончена',
} satisfies Record<ChartGranularity, string>;

const chartTitle = computed(
  () => props.title ?? `Выручка ${GRANULARITY_LABELS[props.granularity]} с вкладом каждой локации`,
);
const previousLabel = computed(() => COMPARISON_MODE_LABELS[AnalyticsComparisonMode.PreviousPeriod].previous);
const formatDateLabel = (date: string): string =>
  props.granularity === 'Hour' ? formatChartAxisLabel(date, 'Hour') : formatChartPointWindow(date, props.granularity);

const activeBreakdown = computed(() => {
  const index = activeIndex.value;

  if (index === null) {
    return [];
  }

  return normalizedSeries.value.map((currentSeries, seriesIndex) => ({
    color: chartSeriesColor(seriesIndex),
    key: currentSeries.key,
    label: currentSeries.label,
    value: currentSeries.values[index] ?? 0,
  }));
});
</script>

<template>
  <ChartCanvas
    v-bind="canvas"
    :geometry="geometry"
    :ticks="valueTicks"
    :scale="yForValue"
    :formatter="valueFormatter"
  >
    <path
      v-for="layer in stackedLayers"
      :key="layer.key"
      class="transition-opacity duration-150 ease-out motion-reduce:transition-none"
      :d="layer.path"
      :fill="layer.color"
      :opacity="layerOpacity(layer.key)"
      aria-hidden="true"
    />

    <path
      v-if="previousPath"
      :d="previousPath"
      fill="none"
      stroke="var(--base-secondary-text-color)"
      stroke-width="1.5"
      stroke-dasharray="4 4"
      stroke-linecap="round"
      vector-effect="non-scaling-stroke"
      aria-hidden="true"
    />

    <rect
      v-if="incompleteSegment"
      :x="incompleteSegment.x"
      :y="geometry.plotTop"
      :width="incompleteSegment.width"
      :height="geometry.plotHeight"
      fill="var(--base-container-bg)"
      opacity="0.45"
      aria-hidden="true"
    />

    <g aria-hidden="true">
      <text
        v-for="(date, index) in dates"
        v-show="visibleLabelIndexes.has(index)"
        :key="date"
        :x="xForIndex(index)"
        :y="geometry.xLabelY"
        text-anchor="middle"
        fill="var(--base-popup-secondary-color)"
        font-size="12"
      >
        {{ formatDateLabel(date) }}
      </text>
    </g>

    <line
      v-if="activePoint"
      :x1="activePoint.x"
      :x2="activePoint.x"
      :y1="geometry.plotTop"
      :y2="geometry.plotBottom"
      stroke="var(--base-text-color)"
      stroke-dasharray="3 4"
      stroke-width="1"
      opacity="0.6"
      vector-effect="non-scaling-stroke"
      aria-hidden="true"
    />
    <template #tooltip>
      <ChartTooltip
        v-if="activePoint"
        :anchor-percent="(activePoint.x / geometry.viewWidth) * 100"
        :viewport-x="activeViewportAnchor?.x"
        :viewport-y="activeViewportAnchor?.y"
        panel
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="font-medium">{{ formatChartPointWindow(activePoint.date, granularity) }}</p>
            <p
              v-if="activePoint.isIncomplete"
              class="mt-0.5 text-pretty text-(--base-popup-secondary-color)"
            >
              {{ INCOMPLETE_LABELS[granularity] }}
            </p>
          </div>
          <p class="font-semibold tabular-nums">{{ valueFormatter(activePoint.total) }}</p>
        </div>

        <ul class="mt-2.5 grid gap-1.5">
          <li
            v-for="item in activeBreakdown"
            :key="item.key"
            class="flex min-w-0 items-center justify-between gap-3"
          >
            <span class="flex min-w-0 items-center gap-2">
              <span
                class="size-2.5 shrink-0 rounded-full"
                :style="{ backgroundColor: item.color }"
              />
              <span class="truncate text-(--base-popup-secondary-color)">{{ item.label }}</span>
            </span>
            <span class="shrink-0 font-medium tabular-nums">{{ valueFormatter(item.value) }}</span>
          </li>
          <li
            v-if="activePoint.previous !== null"
            class="flex min-w-0 items-center justify-between gap-3 border-t border-(--base-divider-soft) pt-1.5"
          >
            <span class="flex min-w-0 items-center gap-2">
              <span class="w-2.5 shrink-0 border-t-2 border-dashed border-(--base-secondary-text-color)" />
              <span class="truncate text-(--base-popup-secondary-color)">{{ previousLabel }}</span>
            </span>
            <span class="shrink-0 font-medium tabular-nums">{{ valueFormatter(activePoint.previous) }}</span>
          </li>
        </ul>
      </ChartTooltip>
    </template>
  </ChartCanvas>
</template>
